<template>
  <div v-if="$userInfo.isSupportUser"
    class="tile is-parent pl-1">
    <section class="tile is-child box">
      <div>
        <h3 class="subtitle mb-5">Sub Stages Setup</h3>
      </div>
      <div class="columns">
        <div class="column is-four-fifths">
          <div class="columns">
            <div class="column is-half">
              <span class="subtitle">Job Stage</span>
            </div>
            <div class="column is-half">
              <span class="subtitle">Sub Job Stage</span>
            </div>
          </div>
          <!-- Default Sub Job Stages (Non-editable) -->
          <div v-if="defaultStages.length > 0">
            <div v-for="(stage, key) in defaultStages"
              :key="key">
              <div class="pb-3">
                <div class="box is-shadowless default-stage-box has-border">
                  <div class="columns is-vcentered is-gapless ">
                    <div class="column is-half is-flex is-align-items-center">
                      <span class="icon has-text-grey mr-1 default-stage-icon">
                        <span class="mso mso-14px">lock</span>
                      </span>
                      <div class="has-text-weight-bold is-size-6">{{ stage.jobStageName }}</div>
                    </div>
                    <div class="column is-half is-flex is-justify-content-space-between">
                      <div class="pl-3 is-size-6">{{ stage.subJobStageName }}</div>
                      <span class="tag is-small default-stage-tag">
                        Default SubJobStage
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Custom Sub Job Stages (Editable) -->
          <draggable :list="customStages"
            group="customStages"
            @change="() => updateSortKeys()"
            handle=".item-drag-handle">
            <div v-for="(stage, key) in customStages"
              :key="key">
              <div class="pb-3">
                <ExpendableDiv :ref="setExpendableDivRefs"
                  :title="stage.jobStageName"
                  :sub-title="stage.subJobStageName"
                  expended-title="Job Stage"
                  expended-sub-title="Sub Job Stage"
                  :enable-sort="true"
                  :style="{ 'border' : ((!stage.jobStage || !stage.subJobStageId) ?'1px solid #D92550':'') }">
                  <div class="columns">
                    <div class="column is-half">
                      <div class="control">
                        <div class="select is-fullwidth"
                          :class="{ 'is-danger' : !stage.jobStage}">
                          <select v-model="stage.jobStage"
                            @change="fetchSubJobStages(stage)">
                            <option v-for="item in jobStages"
                              :value="item.stage"
                              :key="item.stage"
                              :disabled="!hasAvailableSubJobStages(item.stage, stage)">
                              {{ item.description }}
                            </option>
                          </select>
                          <span class="help is-danger"
                            v-if="!stage.jobStage">Field is required.</span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-half">
                      <div class="control is-flex is-align-items-end is-flex-direction-column">
                        <div class="select is-fullwidth"
                          :class="{ 'is-danger' : !stage.subJobStageId}">
                          <select v-model="stage.subJobStageId">
                            <option v-for="item in stage.subJobStageOptions"
                              :value="item.subJobStageId"
                              :key="item.subJobStageId">
                              {{ item.subJobStageName }}
                            </option>
                          </select>
                        </div>
                        <div :class="{ 'is-flex is-justify-content-space-between is-align-items-center' : !stage.subJobStageId}"
                          :style="{ width: !stage.subJobStageId ? '100%' : '' }">
                          <span class="help is-danger"
                            v-if="!stage.subJobStageId">Field is required.</span>
                          <span class="icon has-text-danger"
                            @click="removeStage(key)"
                            @mousedown="deletingStageKey = key"
                            @mouseup="deletingStageKey = null"
                            @mouseleave="deletingStageKey = null">
                            <span class="mso mso-20px"
                              :class="{'mso-fill': deletingStageKey === key}">
                              Delete</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </ExpendableDiv>
              </div>
            </div>
          </draggable>
          <div class="button is-primary is-inverted is-fullwidth"
            style="border: 1px solid #e7e8eb; border-radius: 6px"
            @click="addStage">
            <span class="icon mr-1">
              <span class="mso mso-20px">Add</span>
            </span>
            Add New
          </div>
        </div>
        <div class="column is-one-fifths">
          <h3 class="subtitle">Available Sub Stages</h3>
          <div class="p-4 mb-2"
            :class="`has-background-${$theme.name}`"
            v-if="entitySummaries.length === 0"
            style="font-size: 0.9em">
            <div class="is-flex is-align-items-center">
              <div>
                <p>No Available Sub Stages.</p>
                <p>Click Add New to Set Up.</p>
              </div>
            </div>
          </div>
          <ul v-else>
            <li v-for="(subStages, jobStageName) in groupedStages"
              :key="jobStageName"
              class="is-flex"
              :class="`has-background-${$theme.name}`">
              <div>
                <span class="job-stage has-text-weight-bold">
                  {{ jobStageName }}
                </span>
                <ul class="pl-5">
                  <li v-for="name in subStages"
                    :key="name">- {{ name }}</li>
                </ul>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </section>
  </div>
  <div v-else
    class="is-flex is-justify-content-center pt-5">
    <div class="notification is-danger is-flex is-justify-content-center is-size-4"
      style="width: 50%">You do not have permissions on this page</div>
  </div>
</template>

<script>
import QuoteService from '../../quote/QuoteService'
import ExpendableDiv from '../components/ExpendableDiv'
import SubJobStageService from '../../subjobstage/SubJobStageService'
import CompanyService from '../../company/CompanyService'
import draggable from 'vuedraggable'
export default {
  name: 'SubJobStageSetup',

  components: {
    ExpendableDiv,
    draggable
  },

  data: () => {
    return {
      showSave: true,
      jobStages: [],
      subJobStages: [],
      defaultStages: [],
      customStages: [],
      repxRepairStages: [],
      deletingStageKey: null,
      filter: {
        jobStageName: '',
        subJobStageName: '',
        sortColumn: 'sortKey',
        sortOrder: 'asc',
        pageIndex: 1,
        pageSize: 18
      }
    }
  },
  computed: {
    entitySummaries() {
      return [...this.defaultStages, ...this.customStages]
    },
    groupedStages() {
      const grouped = {}

      this.entitySummaries.forEach((stage) => {
        if (!stage.jobStageName) return
        if (!grouped[stage.jobStageName]) {
          grouped[stage.jobStageName] = []
        }
        grouped[stage.jobStageName].push(stage.subJobStageName || '(Unselected)')
      })
      return grouped
    },
    serilaizeData() {
      const serializedData = Object.entries(this.entitySummaries).map(([key, value]) => ({
        StageName: key,
        Stages: value
      }))
      return serializedData
    }
  },
  watch: {
    entitySummaries: {
      handler(val) {
        val.forEach((stage) => {
          this.$watch(
            () => stage.jobStage,
            () => {
              // Only call fetchSubJobStages for custom stages (not default stages)
              if (stage.isDefault !== true) {
                this.fetchSubJobStages(stage)
              }
            },
            { immediate: true }
          )
        })
      },
      deep: true,
      immediate: true
    }
  },
  async created() {
    this.refresh()
  },
  mounted() {},
  methods: {
    async refresh() {
      await this.fetchJobStages()
      await this.fetchAllSubJobStages()
      await this.fetchRepxRepairStages()
      await this.getData()
      this.resetExpendableDivs()
    },
    async getData() {
      const allStages = await SubJobStageService.getSubJobStages(this.filter)
      allStages.forEach((item, index) => {
        if (item.sortKey === null) {
          item.sortKey = index + 1
        }
      })
      this.defaultStages = allStages.filter((stage) => stage.isDefault === true)
      this.customStages = allStages.filter((stage) => stage.isDefault !== true)
    },
    async fetchJobStages() {
      this.jobStages = await QuoteService.getJobStages()
    },
    async fetchAllSubJobStages() {
      this.subJobStages = await SubJobStageService.getAllActiveSubJobStage()
    },
    async fetchRepxRepairStages() {
      this.repxRepairStages = await CompanyService.getRepxStagesSetting()
    },
    async fetchSubJobStages(stage) {
      // Skip processing for default stages as they shouldn't be modified
      if (stage.isDefault === true) {
        return
      }

      if (stage.jobStage) {
        const selectedIds = this.entitySummaries
          .filter((s) => s.subJobStageId !== stage.subJobStageId && s.jobStage === stage.jobStage)
          .map((s) => s.subJobStageId)
        const currentId = stage.subJobStageId
        stage.subJobStageOptions = this.subJobStages
          .filter((item) => item.jobStage == stage.jobStage && (item.subJobStageId === currentId || !selectedIds.includes(item.subJobStageId)))
          .sort((a, b) => a.sortKey - b.sortKey)

        const selected = stage.subJobStageOptions.find((s) => s.subJobStageId === currentId)
        stage.subJobStageName = selected ? selected.subJobStageName : ''
        if (!selected) {
          stage.subJobStageId = ''
        }

        const jobMatch = this.jobStages.find((j) => j.stage === stage.jobStage)
        stage.jobStageName = jobMatch ? jobMatch.description : ''
      } else {
        stage.subJobStageOptions = []
        stage.subJobStageId = ''
        stage.subJobStageName = ''
        stage.jobStageName = ''
      }
    },
    async addStage() {
      const newentity = await SubJobStageService.getNewEntity()
      this.customStages.push(newentity)
      this.updateSortKeys()

      // Wait for the DOM to update, then expand the newly added item
      await this.$nextTick()

      // Find the index of the new item in customStages (it will be the last one)
      const newItemIndex = this.customStages.length - 1

      // Expand the corresponding ExpendableDiv
      if (this.$refs.expendableDivRefs && this.$refs.expendableDivRefs[newItemIndex]) {
        const expandableDiv = this.$refs.expendableDivRefs[newItemIndex]
        if (!expandableDiv.isExpanded) {
          expandableDiv.toggle()
        }
      }
    },
    removeStage(index) {
      // Find the actual stage in entitySummaries array
      const targetStage = this.customStages[index]

      if (!targetStage) {
        return
      }

      if (targetStage.isInUse) {
          this.$toast.open({
            message: 'This sub-job stage cannot be deleted because it is linked to one or more quotes.',
            type: 'is-danger',
            position: 'is-top',
            queue: false
          })
          return
      }

      if (!targetStage.isNew) {
        const isMapped = this.repxRepairStages.some((stage, i) => i !== index && stage.subJobStageId === targetStage.subJobStageId)

        if (isMapped) {
          this.$toast.open({
            message: 'This Sub Stage is already used in RepX mapping.',
            type: 'is-danger',
            position: 'is-top',
            queue: false
          })
          return
        }
      }

      // Find the correct index in entitySummaries array
      const entityIndex = this.customStages.findIndex((stage) => stage === targetStage)
      if (entityIndex !== -1) {
        this.customStages.splice(entityIndex, 1)
      }

      this.updateSortKeys()
    },
    hasAvailableSubJobStages(jobStage, currentStage) {
      // Get all sub job stages for this job stage
      const subJobStagesForJobStage = this.subJobStages.filter((item) => item.jobStage == jobStage)

      if (subJobStagesForJobStage.length === 0) {
        return false
      }

      // Get IDs of sub job stages already selected by other stages with the same job stage
      const selectedIds = this.entitySummaries
        .filter((s) => s.subJobStageId !== currentStage.subJobStageId && s.jobStage === jobStage)
        .map((s) => s.subJobStageId)

      // Check if there are any available sub job stages (not already selected)
      const availableSubJobStages = subJobStagesForJobStage.filter(
        (item) => item.subJobStageId === currentStage.subJobStageId || !selectedIds.includes(item.subJobStageId)
      )

      return availableSubJobStages.length > 0
    },
    async save() {
      const unmapped = this.entitySummaries.some((stage) => !stage.jobStage || !stage.subJobStageId)
      if (unmapped) {
        this.$toast.open({
          message: 'Make sure all sub job stages are mapped',
          type: 'is-danger',
          position: 'is-top'
        })
        return false
      }

      try {
        this.$showSpinner()
        const result = await SubJobStageService.updateSubJobStagesSetup(this.entitySummaries)

        const { isSuccess, message } = result
        if (!isSuccess && message) {
          this.$toast.open({
            message: message,
            type: 'is-danger',
            position: 'is-top',
            queue: false
          })
          return
        }
        this.$toast.open({
          message: 'Sub Stages Setup Updated',
          type: 'is-success',
          position: 'is-top',
          queue: false
        })
        this.refresh()
        for (const stage of this.entitySummaries) {
          await this.fetchSubJobStages(stage)
        }
      } catch (error) {
        this.$toast.open({
          message: 'Failed Sub Stages Setup Updated',
          type: 'is-danger',
          position: 'is-top',
          queue: false
        })
      } finally {
        this.$hideSpinner()
      }
    },
    updateSortKeys() {
      this.entitySummaries.forEach((stage, index) => {
        stage.sortKey = index + 1
      })
    },
    setExpendableDivRefs(el) {
      if (!this.$refs.expendableDivRefs) {
        this.$refs.expendableDivRefs = []
      }
      if (el) {
        this.$refs.expendableDivRefs.push(el)
      }
    },
    resetExpendableDivs() {
      if (!this.$refs.expendableDivRefs) {
        return
      }
      this.$refs.expendableDivRefs.forEach((i) => {
        if (i.isExpanded) i.toggle()
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.job-stage {
  display: block;
  width: 100%;
  padding: 0.5em;
  font-size: 0.9em;
}

.default-stage-box {
  padding: 0.5rem;
  border: 1px solid var(--border-color);
}

.default-stage-columns {
  margin-bottom: 0;
  min-height: 2rem;
}

.default-stage-icon {
  width: 0.875rem;
  height: 0.875rem;
}

.default-stage-tag {
  height: 1.5rem;
  font-size: 0.65rem;
}

.tile.is-vertical {
  margin-left: 1rem;
}
.tile.is-vertical > .tile.is-child:not(:last-child) {
  margin-bottom: 0.5rem !important;
}
.tile.is-ancestor.no-margin {
  padding-left: 0.5rem;
}
.draggable-box {
  background-color: var(--draggable-box-background-color);
}
</style>
